import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Lock, Check, ChevronRight } from "lucide-react";
import { useSuccessionStageState, humanSuccessionStatus } from "@/lib/succession/stages";
import { StewardReviewPanel } from "@/components/succession/StewardReviewPanel";

type StageAction = { kind: "navigate"; href: string } | { kind: "review" };

const STAGES: ReadonlyArray<{ key: string; label: string; teach: string; action: StageAction }> = [
  { key: "profile", label: "Profile", teach: "Your family's succession picture", action: { kind: "navigate", href: "/succession" } },
  { key: "path", label: "Path", teach: "Your successor's learning and welcome", action: { kind: "review" } },
  { key: "decisions", label: "Decisions", teach: "The choices that shape your plan", action: { kind: "navigate", href: "/succession/decisions" } },
  { key: "documents", label: "Your documents", teach: "Your resolutions and letters, made ready", action: { kind: "navigate", href: "/succession/documents" } },
  { key: "execution", label: "Execution", teach: "Where things get signed, filed, and set up", action: { kind: "navigate", href: "/succession/execution" } },
  { key: "rhythm", label: "Rhythm", teach: "The yearly drill that proves it works", action: { kind: "navigate", href: "/succession/rhythm" } },
];

export default function SuccessionOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const s = useSuccessionStageState(user?.id);
  const [reviewOpen, setReviewOpen] = useState(false);

  if (s.loading) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  const unlocked = [true, s.stage2Unlocked, s.stage3Unlocked, s.stage4Unlocked, s.stage5Unlocked, s.stage6Unlocked];
  const done = [s.stage1Done, s.stage2Done, s.stage3Done, s.stage4Done, s.stage5Done, s.stage6Done];

  const allDone = done.every(Boolean);
  const profileLabel = humanSuccessionStatus(s.profileStatus);
  const successorLabel = humanSuccessionStatus(s.successorStatus);

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/governance")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Governance
        </Button>
        <div>
          <h1 className="text-3xl font-serif">Succession</h1>
          <p className="text-muted-foreground mt-2">Getting the next steward of your family's Association ready.</p>
          <div className="flex items-center gap-2 mt-3">
            <Badge variant="outline" className="text-xs">Profile: {profileLabel}</Badge>
            {s.successorStatus && (
              <Badge variant="outline" className="text-xs">Successor: {successorLabel}</Badge>
            )}
          </div>
        </div>

        {allDone && (
          <Card className="p-4 border-emerald-500/40 bg-emerald-500/5">
            <p className="text-sm"><strong>Your succession plan is alive.</strong> Keep the rhythm.</p>
          </Card>
        )}

        {s.isPaused && (
          <Card className="p-4 border-amber-300 bg-amber-50">
            <p className="text-sm text-amber-900">Your succession record is paused for review. Our team will reach out.</p>
          </Card>
        )}

        <div className="space-y-3">
          {STAGES.map((st, i) => {
            const isUnlocked = unlocked[i];
            const isDone = done[i];
            const onOpen = () => {
              if (!isUnlocked) return;
              if (st.action.kind === "review") {
                setReviewOpen(true);
              } else {
                navigate(st.action.href);
              }
            };
            return (
              <Card key={st.key} className={`p-4 ${isUnlocked ? "" : "opacity-60"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{i + 1}. {st.label}</span>
                      {isDone && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs">Done</Badge>}
                      {!isUnlocked && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{st.teach}</p>
                  </div>
                  <Button
                    variant={isDone ? "outline" : "default"}
                    size="sm"
                    disabled={!isUnlocked}
                    onClick={onOpen}
                  >
                    {isDone ? <Check className="h-4 w-4 mr-2" /> : null}
                    {isDone ? "Review" : "Open"}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {s.unaId && (
          <StewardReviewPanel
            open={reviewOpen}
            onOpenChange={setReviewOpen}
            associationId={s.unaId}
            associationName={s.unaName}
            onCompleted={() => { /* stage state refreshes on next mount */ }}
          />
        )}
      </div>
    </MainLayout>
  );
}
